import { useEffect, useRef } from "react";
import { ClerkProvider, SignIn, SignUp, Show, useClerk, useUser } from "@clerk/react";
import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Sentry } from "@/lib/sentry";

import { AppLayout } from "./components/layout/AppLayout";
import { AuthLayout } from "./components/layout/AuthLayout";
import Home from "./pages/home";
import Dashboard from "./pages/dashboard";
import DealsList from "./pages/deals/index";
import NewDeal from "./pages/deals/new";
import NewLocalDeal from "./pages/deals/new-local";
import DealDetail from "./pages/deals/[id]";
import LocalDealDetail from "./pages/deals/local-[id]";
import ApprovalsPage from "./pages/deals/approvals";
import RegistrationsList from "./pages/registrations/index";
import RegistrationDetail from "./pages/registrations/[id]";
import TemplatesList from "./pages/templates/index";
import TemplateEdit from "./pages/templates/[id]-edit";
import Settings from "./pages/settings";
import AuditLogPage from "./pages/audit-log";
import SuppliersPage from "./pages/suppliers";
import SupportPage from "./pages/support/index";
import TicketDetail from "./pages/support/[id]";
import AdminTickets from "./pages/support/admin";
import Reports from "./pages/reports";
import NotFound from "./pages/not-found";

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const authBypass = import.meta.env.VITE_AUTH_BYPASS === "true";
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!authBypass && !clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env file");
}

const clerkAppearance = {
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.svg`,
  },
  variables: {
    colorPrimary: "hsl(187, 64%, 43%)",
    colorBackground: "hsl(0, 0%, 100%)",
    colorInputBackground: "hsl(210, 20%, 98%)",
    colorText: "hsl(215, 28%, 12%)",
    colorTextSecondary: "hsl(215, 16%, 42%)",
    colorInputText: "hsl(215, 28%, 12%)",
    colorNeutral: "hsl(214, 20%, 90%)",
    borderRadius: "0.5rem",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontFamilyButtons: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  elements: {
    rootBox: "w-full",
    cardBox: "shadow-lg border border-border rounded-2xl w-full overflow-hidden",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: { color: "hsl(215, 28%, 12%)" },
    headerSubtitle: { color: "hsl(215, 16%, 42%)" },
    socialButtonsBlockButtonText: { color: "hsl(215, 28%, 12%)" },
    formFieldLabel: { color: "hsl(215, 28%, 12%)" },
    footerActionLink: { color: "hsl(187, 64%, 43%)" },
    footerActionText: { color: "hsl(215, 16%, 42%)" },
    dividerText: { color: "hsl(215, 16%, 42%)" },
    formFieldSuccessText: { color: "hsl(215, 28%, 12%)" },
    alertText: { color: "hsl(0, 84.2%, 60.2%)" },
    formButtonPrimary: "bg-primary hover:bg-primary/90 text-primary-foreground",
    otpCodeFieldInputs: "!gap-1.5 sm:!gap-2 !flex !flex-nowrap !justify-center !w-full",
    otpCodeFieldInput:
      "!border !border-border !bg-muted/40 !text-foreground !rounded-md " +
      "!h-11 !w-9 !text-base sm:!h-12 sm:!w-12 sm:!text-lg !font-medium !p-0 !min-w-0 " +
      "focus:!border-primary focus:!ring-2 focus:!ring-primary/30 focus:!outline-none " +
      "focus-visible:!border-primary focus-visible:!ring-2 focus-visible:!ring-primary/30",
  },
};

const authFormAppearance = {
  ...clerkAppearance,
  options: {
    ...clerkAppearance.options,
    logoPlacement: "inside" as const,
  },
  elements: {
    ...clerkAppearance.elements,
    rootBox: "w-full",
    cardBox: "w-full shadow-none border-0",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    logoBox: "flex justify-center mb-4",
    logoImage: "h-10 w-auto",
  },
};

function SignInPage() {
  return (
    <AuthLayout>
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        appearance={authFormAppearance}
      />
    </AuthLayout>
  );
}

function SignUpPage() {
  return (
    <AuthLayout>
      <SignUp
        routing="path"
        path={`${basePath}/sign-up`}
        signInUrl={`${basePath}/sign-in`}
        appearance={authFormAppearance}
      />
    </AuthLayout>
  );
}

function HomeRedirect() {
  return (
    <>
      <Show when="signed-in">
        <Redirect to="/dashboard" />
      </Show>
      <Show when="signed-out">
        <Home />
      </Show>
    </>
  );
}

function AppRoutes() {
  return (
    <AppLayout>
      <Switch>
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/deals" component={DealsList} />
        <Route path="/deals/approvals" component={ApprovalsPage} />
        <Route path="/deals/new" component={NewDeal} />
        <Route path="/deals/new-local" component={NewLocalDeal} />
        <Route path="/deals/local/:id" component={LocalDealDetail} />
        <Route path="/deals/:id" component={DealDetail} />
        <Route path="/registrations" component={RegistrationsList} />
        <Route path="/registrations/:id" component={RegistrationDetail} />
        <Route path="/templates" component={TemplatesList} />
        <Route path="/templates/new" component={TemplateEdit} />
        <Route path="/templates/:id/edit" component={TemplateEdit} />
        <Route path="/suppliers" component={SuppliersPage} />
        <Route path="/settings" component={Settings} />
        <Route path="/reports" component={Reports} />
        <Route path="/support/admin" component={AdminTickets} />
        <Route path="/support/:id" component={TicketDetail} />
        <Route path="/support" component={SupportPage} />
        <Route path="/admin/audit-log" component={AuditLogPage} />
        <Route component={NotFound} />
      </Switch>
    </AppLayout>
  );
}

function AuthGuard() {
  return (
    <>
      <Show when="signed-in">
        <AppRoutes />
      </Show>
      <Show when="signed-out">
        <Redirect to="/sign-in" />
      </Show>
    </>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);

  return null;
}

function ClerkSentryUserBinder() {
  const { addListener } = useClerk();
  const { isLoaded, isSignedIn, user } = useUser();

  // Subscribe to subsequent user changes (sign-in / sign-out / switch).
  useEffect(() => {
    const unsubscribe = addListener(({ user: u }) => {
      if (u?.id) {
        const email =
          u.primaryEmailAddress?.emailAddress ??
          u.emailAddresses?.[0]?.emailAddress ??
          undefined;
        Sentry.setUser(email ? { id: u.id, email } : { id: u.id });
      } else {
        Sentry.setUser(null);
      }
    });
    return unsubscribe;
  }, [addListener]);

  // Set the initial user as soon as Clerk has loaded, in case the listener
  // does not emit an event for the already-restored session on mount.
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && user?.id) {
      const email =
        user.primaryEmailAddress?.emailAddress ??
        user.emailAddresses?.[0]?.emailAddress ??
        undefined;
      Sentry.setUser(email ? { id: user.id, email } : { id: user.id });
    } else {
      Sentry.setUser(null);
    }
  }, [isLoaded, isSignedIn, user]);

  return null;
}

const queryClient = new QueryClient();

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      localization={{
        signIn: {
          start: {
            title: "TDM Registration App",
            subtitle: "Sign in to manage deal registrations",
          },
        },
        signUp: {
          start: {
            title: "Join TDM Registration App",
            subtitle: "Create your account to get started",
          },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <ClerkSentryUserBinder />
        <Switch>
          <Route path="/" component={HomeRedirect} />
          <Route path="/sign-in/*?" component={SignInPage} />
          <Route path="/sign-up/*?" component={SignUpPage} />
          <Route path="/*">
            <AuthGuard />
          </Route>
        </Switch>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function BypassRoutes() {
  return (
    <QueryClientProvider client={queryClient}>
      <Switch>
        <Route path="/sign-in/*?">
          <Redirect to="/dashboard" />
        </Route>
        <Route path="/sign-up/*?">
          <Redirect to="/dashboard" />
        </Route>
        <Route path="/" component={() => <Redirect to="/dashboard" />} />
        <Route path="/*">
          <AppRoutes />
        </Route>
      </Switch>
    </QueryClientProvider>
  );
}

function App() {
  return (
    <TooltipProvider>
      <WouterRouter base={basePath}>
        {authBypass ? <BypassRoutes /> : <ClerkProviderWithRoutes />}
      </WouterRouter>
      <Toaster />
    </TooltipProvider>
  );
}

export default App;
